import React, { useEffect, useState } from "react";
import { fetchMyRecentMatches } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DisputeMatchModal } from "./DisputeMatchModal";
import { MatchRecapCardModal } from "./MatchRecapCardModal";

/**
 * RecentResultsBlock — last few completed matches across every team I'm on.
 * Each row exposes a "DISPUTE" button (or "VIEW DISPUTE" if one is already
 * filed) that opens DisputeMatchModal — the natural home for Wave C's
 * dispute UI v2. PRO users also see a "RECAP" button that pops the
 * shareable PNG card from Wave D.
 */
export function RecentResultsBlock({ onToast, refreshKey = 0 }) {
  const { user } = useAuth();
  const isPro = !!(user?.is_pro || ["pro", "elite"].includes((user?.plan || "").toLowerCase()));
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [disputing, setDisputing] = useState(null);
  const [recapingId, setRecapingId] = useState(null);

  const load = () => {
    setBusy(true);
    fetchMyRecentMatches(6)
      .then((r) => { setRows(r); setBusy(false); })
      .catch(() => setBusy(false));
  };

  useEffect(() => { load(); }, [refreshKey]);

  if (!busy && rows.length === 0) {
    return (
      <div className="glass rounded-3xl p-4" data-testid="dash-recent-results">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold mb-2">
          <i className="fas fa-scale-balanced mr-1" />RECENT RESULTS
        </p>
        <p className="text-[12px] text-white/45 text-center py-2">
          No completed matches yet. Once you submit a score it'll show up here.
        </p>
      </div>
    );
  }

  return (
    <div className="glass rounded-3xl p-4" data-testid="dash-recent-results">
      <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold mb-3">
        <i className="fas fa-scale-balanced mr-1" />RECENT RESULTS · {rows.length}
      </p>
      <div className="space-y-2">
        {rows.map((m) => {
          const a = m.from_team?.name || "Team A";
          const b = m.to_team?.name || "Team B";
          const winnerIsA = m.winner_team_id === m.from_team?.team_id;
          return (
            <div key={m.challenge_id}
                 data-testid={`recent-result-${m.challenge_id}`}
                 className={`rounded-2xl p-3 border ${m.disputed ? "bg-[#14b8a6]/10 border-[#14b8a6]/40" : "bg-black/30 border-white/10"}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-sm font-bold text-white truncate">
                  <span className={winnerIsA ? "text-[#14b8a6]" : "text-white/65"}>{a}</span>
                  <span className="text-white/40 mx-1.5 tabular-nums">{m.score_a}-{m.score_b}</span>
                  <span className={!winnerIsA ? "text-[#14b8a6]" : "text-white/65"}>{b}</span>
                </p>
                {m.disputed ? (
                  <button
                    type="button"
                    onClick={() => setDisputing(m)}
                    data-testid={`recent-view-dispute-${m.challenge_id}`}
                    className="text-[10px] tracking-widest px-3 py-1 rounded-lg bg-[#14b8a6]/20 text-[#14b8a6] border border-[#14b8a6]/50 hover:bg-[#14b8a6]/30">
                    <i className="fas fa-eye mr-1" />VIEW DISPUTE
                  </button>
                ) : m.can_dispute ? (
                  <div className="flex gap-1.5">
                    {isPro && (
                      <button
                        type="button"
                        onClick={() => setRecapingId(m.challenge_id)}
                        data-testid={`recent-recap-${m.challenge_id}`}
                        title="Generate a shareable PNG recap"
                        className="text-[10px] tracking-widest px-3 py-1 rounded-lg bg-[#14b8a6]/15 text-[#a39cff] border border-[#14b8a6]/40 hover:bg-[#14b8a6]/25">
                        <i className="fas fa-image mr-1" />RECAP
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setDisputing(m)}
                      data-testid={`recent-dispute-${m.challenge_id}`}
                      className="text-[10px] tracking-widest px-3 py-1 rounded-lg bg-white/5 text-white/55 border border-white/15 hover:bg-[#14b8a6]/15 hover:text-[#14b8a6] hover:border-[#14b8a6]/40">
                      <i className="fas fa-flag mr-1" />DISPUTE
                    </button>
                  </div>
                ) : null}
              </div>
              <p className="text-[10px] text-[#a3a3ac]/85 truncate mt-1">
                <i className="fas fa-trophy mr-1" />{m.league?.name || "—"}
              </p>
            </div>
          );
        })}
      </div>
      {disputing && (
        <DisputeMatchModal
          challenge={disputing}
          onClose={() => setDisputing(null)}
          onChanged={load}
          onToast={onToast}
        />
      )}
      <MatchRecapCardModal
        challengeId={recapingId}
        open={!!recapingId}
        onClose={() => setRecapingId(null)}
        onToast={onToast}
      />
    </div>
  );
}

export default RecentResultsBlock;
